import { useState, type ReactNode, type ElementType } from 'react';
import { Box, Paper, Stack, Typography, Tooltip, IconButton, Chip, Divider, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import TimeHelper from '../../../utils/time';
import { SCUIZoomableImage } from '../../../atomic/atoms';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import JoinInnerOutlinedIcon from '@mui/icons-material/JoinInnerOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

type FieldConfig = {
    field: string;
    label: string;
    icon: ElementType;
    accent: string;
    accentBg: string;
    fullWidthValue?: boolean;
    render?: (val: any, item?: any) => ReactNode;
};

export const ItemPaper = ({ item, fullWidth = true }: any) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const { t } = useTranslation();

    const itemWithSpecialCode = item?.barcode
        ? { ...item, specialColorCode: `${item.specialCode}-${item.colorCode}` }
        : null;

    const booleanChip = (val: boolean) => (
        <Chip
            size="small"
            icon={val ? <CheckCircleOutlineIcon /> : <CancelOutlinedIcon />}
            label={val ? t('general.yes') : t('general.no')}
            sx={{
                height: 28,
                fontWeight: 700,
                fontSize: '0.8rem',
                color: val ? '#15803D' : '#DC2626',
                backgroundColor: val ? '#DCFCE7' : '#FEE2E2',
                border: `1px solid ${val ? '#86EFAC' : '#FCA5A5'}`,
                '& .MuiChip-icon': {
                    color: 'inherit',
                    fontSize: '1.05rem',
                    ml: '6px',
                },
            }}
        />
    );

    const scalarFields: FieldConfig[] = [
        {
            field: 'barcode',
            label: 'studio.barcode',
            icon: QrCode2OutlinedIcon,
            accent: '#2563EB',
            accentBg: '#EFF6FF',
        },
        {
            field: 'specialColorCode',
            label: 'studio.specialColorCode',
            icon: PaletteOutlinedIcon,
            accent: '#7C3AED',
            accentBg: '#F5F3FF',
        },
        {
            field: 'size',
            label: 'studio.size',
            icon: StraightenOutlinedIcon,
            accent: '#0891B2',
            accentBg: '#ECFEFF',
        },
        {
            field: 'productName',
            label: 'studio.productName',
            icon: LocalOfferOutlinedIcon,
            accent: '#DB2777',
            accentBg: '#FDF2F8',
        },
        {
            field: 'productAcceptanceDate',
            label: 'studio.date',
            icon: CalendarMonthOutlinedIcon,
            accent: '#0EA5E9',
            accentBg: '#F0F9FF',
            render: (val: any) => TimeHelper.formatDateHour(val),
        },
        {
            field: 'line',
            label: 'studio.itemAcceptanceCancellation.lineInfo',
            icon: TimelineOutlinedIcon,
            accent: '#F59E0B',
            accentBg: '#FFFBEB',
        },
        {
            field: 'description',
            label: 'studio.description',
            icon: NotesOutlinedIcon,
            accent: '#6B7280',
            accentBg: '#F3F4F6',
            fullWidthValue: true,
        },
    ];

    const booleanFields: FieldConfig[] = [
        {
            field: 'isInStock',
            label: 'studio.itemAcceptanceCancellation.stockStatus',
            icon: Inventory2OutlinedIcon,
            accent: '#16A34A',
            accentBg: '#F0FDF4',
            render: (val: boolean) => booleanChip(val),
        },
        {
            field: 'isFastTrack',
            label: 'studio.itemAcceptanceCancellation.orderType',
            icon: BoltOutlinedIcon,
            accent: '#F97316',
            accentBg: '#FFF7ED',
            render: (val: boolean) => booleanChip(val),
        },
        {
            field: 'isPartOfSet',
            label: 'studio.itemAcceptanceCancellation.isPartOfSet',
            icon: CategoryOutlinedIcon,
            accent: '#8B5CF6',
            accentBg: '#F5F3FF',
            render: (val: boolean) => booleanChip(val),
        },
        {
            field: 'isMatchingSet',
            label: 'studio.itemAcceptanceCancellation.matchingSet',
            icon: JoinInnerOutlinedIcon,
            accent: '#6366F1',
            accentBg: '#EEF2FF',
            render: (val: boolean, row?: any) => {
                if (row?.matchingSetLoading) {
                    return (
                        <LinearProgress
                            sx={{
                                width: '100%',
                                maxWidth: 140,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#E5E7EB',
                                '& .MuiLinearProgress-bar': { borderRadius: 3 },
                            }}
                        />
                    );
                }
                return booleanChip(Boolean(val));
            },
        },
    ];

    const getClipboardText = (cfg: FieldConfig): string => {
        const raw = itemWithSpecialCode?.[cfg.field];
        if (cfg.field === 'productAcceptanceDate') {
            return raw ? TimeHelper.formatDateHour(raw) : '';
        }
        if (raw === undefined || raw === null) return '';
        return String(raw);
    };

    const handleCopy = (fieldName: string, cfg: FieldConfig) => {
        navigator.clipboard.writeText(getClipboardText(cfg));
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 1200);
    };

    if (!itemWithSpecialCode) {
        return null;
    }

    const isAlert =
        itemWithSpecialCode.responseStatus === 2 || itemWithSpecialCode.responseStatus === 3;
    const isMatchingSet =
        itemWithSpecialCode.isMatchingSet === true ||
        itemWithSpecialCode.isMatchingSet === 'true' ||
        itemWithSpecialCode.isMatchingSet === 1 ||
        itemWithSpecialCode.isMatchingSet === '1';

    // Priority: Alert (red) > Matching Set (blue) > FT/Stock (green) > PartOfSet (yellow) > default
    let paperBgColor = '#FAFAFA';
    let borderLeftColor = '#E5E7EB';

    if (isAlert) {
        paperBgColor = '#FECACA';
        borderLeftColor = '#B91C1C';
    } else if (isMatchingSet) {
        paperBgColor = '#DBEAFE';
        borderLeftColor = '#2563EB';
    } else if (itemWithSpecialCode.isFastTrack || itemWithSpecialCode.isInStock) {
        paperBgColor = '#DCFCE7';
        borderLeftColor = '#16A34A';
    } else if (itemWithSpecialCode.isPartOfSet) {
        paperBgColor = '#FEF9C3';
        borderLeftColor = '#EAB308';
    }

    const renderFieldTile = (cfg: FieldConfig) => {
        const Icon = cfg.icon;
        const raw = itemWithSpecialCode[cfg.field];
        const display = cfg.render ? cfg.render(raw) : (raw ?? '—');
        const isStringLike = typeof display === 'string' || typeof display === 'number';

        return (
            <Box
                key={cfg.field}
                sx={{
                    position: 'relative',
                    height: '100%',
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    transition: 'all 0.18s ease',
                    '&:hover': {
                        borderColor: cfg.accent,
                        boxShadow: `0 6px 20px ${cfg.accent}22, 0 2px 6px rgba(0,0,0,0.04)`,
                        transform: 'translateY(-1px)',
                    },
                }}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems={cfg.fullWidthValue ? 'flex-start' : 'center'}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 44,
                            height: 44,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: cfg.accentBg,
                            color: cfg.accent,
                            border: `1px solid ${cfg.accent}33`,
                        }}
                    >
                        <Icon sx={{ fontSize: '1.5rem' }} />
                    </Box>

                    {cfg.field === 'size' ? (
                        <Stack direction="row" spacing={2} sx={{ flex: 1, minWidth: 0 }} divider={<Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}>
                            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        color: '#9CA3AF',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {t(cfg.label)}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: '#111827',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {display}
                                </Typography>
                            </Stack>
                            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        color: '#9CA3AF',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {t('studio.colorName') || 'Renk Adı'}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: '#111827',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {itemWithSpecialCode.colorName || '—'}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    color: '#9CA3AF',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    lineHeight: 1.2,
                                }}
                            >
                                {t(cfg.label)}
                            </Typography>
                            {isStringLike ? (
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: '#111827',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {display}
                                </Typography>
                            ) : (
                                <Box sx={{ mt: 0.5, display: 'inline-flex', alignItems: 'center' }}>
                                    {display}
                                </Box>
                            )}
                        </Stack>
                    )}

                    <Tooltip title={t('general.copy')} arrow placement="top">
                        <IconButton
                            size="small"
                            onClick={() => handleCopy(cfg.field, cfg)}
                            sx={{
                                flexShrink: 0,
                                width: 34,
                                height: 34,
                                color: copiedField === cfg.field ? '#16A34A' : '#9CA3AF',
                                border: '1px solid',
                                borderColor: copiedField === cfg.field ? '#86EFAC' : '#E5E7EB',
                                backgroundColor: copiedField === cfg.field ? '#F0FDF4' : '#F9FAFB',
                                borderRadius: '8px',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    color: cfg.accent,
                                    borderColor: cfg.accent,
                                    backgroundColor: cfg.accentBg,
                                },
                            }}
                        >
                            {copiedField === cfg.field ? (
                                <CheckIcon sx={{ fontSize: '1.1rem' }} />
                            ) : (
                                <ContentCopyOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                            )}
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
        );
    };

    const renderBooleanGroup = () => (
        <Box
            sx={{
                p: 2,
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                transition: 'all 0.18s ease',
                '&:hover': {
                    borderColor: '#CBD5E1',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                },
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'stretch', flexWrap: 'wrap' }}>
                {booleanFields.map((cfg) => {
                    const Icon = cfg.icon;
                    const raw = itemWithSpecialCode[cfg.field];
                    const display = cfg.render ? cfg.render(raw, itemWithSpecialCode) : (raw ?? '—');

                    return (
                        <Stack key={cfg.field} direction="row" spacing={1.5} sx={{ flex: 1, minWidth: { xs: '100%', sm: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' } }}>
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: 44,
                                    height: 44,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: cfg.accentBg,
                                    color: cfg.accent,
                                    border: `1px solid ${cfg.accent}33`,
                                }}
                            >
                                <Icon sx={{ fontSize: '1.5rem' }} />
                            </Box>
                            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        color: '#9CA3AF',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {t(cfg.label)}
                                </Typography>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{display}</Box>
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );

    const scalarWithoutDescription = scalarFields.filter((f) => f.field !== 'description');
    const descriptionField = scalarFields.find((f) => f.field === 'description')!;

    return (
        <Paper
            elevation={0}
            sx={{
                width: fullWidth ? '100%' : '50%',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid #E5E7EB',
                backgroundColor: paperBgColor,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${borderLeftColor}`,
            }}
        >
            {isAlert && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 3,
                        py: 1.25,
                        backgroundColor: '#FECACA',
                        borderBottom: '1px solid #F87171',
                        color: '#991B1B',
                    }}
                >
                    <ErrorOutlineOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                        {itemWithSpecialCode.description || t('general.anErrorOccured')}
                    </Typography>
                </Box>
            )}

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                sx={{ p: { xs: 2, sm: 3 }, alignItems: 'stretch' }}
            >
                <Box
                    sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                    }}
                >
                    {itemWithSpecialCode.productImageUrl ? (
                        <SCUIZoomableImage
                            src={itemWithSpecialCode.productImageUrl}
                            alt=""
                            containerSx={{
                                width: { xs: '100%', sm: 300, md: 320 },
                                maxWidth: '100%',
                                minHeight: 320,
                                borderRadius: '12px',
                                border: '1px solid #E5E7EB',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    borderColor: '#CBD5E1',
                                    '& img': {
                                        transform: 'scale(1.03)',
                                    },
                                },
                            }}
                            imageSx={{
                                maxWidth: '100%',
                                maxHeight: 360,
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                transition: 'transform 0.25s ease-in-out',
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: { xs: '100%', sm: 300, md: 320 },
                                maxWidth: '100%',
                                minHeight: 320,
                                borderRadius: '12px',
                                border: '1px solid #E5E7EB',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                            }}
                        >
                            <Stack alignItems="center" spacing={1} sx={{ color: '#9CA3AF' }}>
                                <ImageNotSupportedIcon sx={{ fontSize: 64 }} />
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                    {t('general.noDataFound')}
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: '#9CA3AF',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                            }}
                        >
                            {t('general.productDetail')}
                        </Typography>
                        <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 1.5,
                        }}
                    >
                        {scalarWithoutDescription.map((cfg) => (
                            <Box key={cfg.field}>{renderFieldTile(cfg)}</Box>
                        ))}

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>{renderBooleanGroup()}</Box>

                        <Box sx={{ gridColumn: descriptionField.fullWidthValue ? { sm: 'span 2' } : 'auto' }}>
                            {renderFieldTile(descriptionField)}
                        </Box>
                    </Box>
                </Box>
            </Stack>
        </Paper>
    );
};
